import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Briefcase, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Layers, 
  DollarSign, 
  Info,
  MoreVertical,
  CheckCircle2,
  Users
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function PostesManagement() {
  const { hasPermission } = usePermissions();
  const [postes, setPostes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPostes();
  }, []);

  const fetchPostes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/rh/postes`, { headers: headers() });
      setPostes(res.data);
    } catch (err) {
      console.error('Error fetching postes:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPostes = useMemo(() => {
    return postes.filter(p => p.nom.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [postes, searchTerm]);

  const handleAddPoste = () => {
    Swal.fire({
      title: 'Ajouter un Poste',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Nom du poste *</label>
            <input id="p-nom" class="rh-modal-input" placeholder="Ex: Développeur Senior">
          </div>
          <div class="rh-input-group">
            <label>Niveau hiérarchique</label>
            <input id="p-niv" type="number" class="rh-modal-input" value="1">
          </div>
          <div class="rh-input-group">
            <label>Salaire de base recommandé (TND)</label>
            <input id="p-sal" type="number" class="rh-modal-input" placeholder="Ex: 2500">
          </div>
          <div class="rh-input-group">
            <label>Description</label>
            <textarea id="p-desc" class="rh-modal-input" placeholder="Responsabilités du poste..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Ajouter',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const nom = document.getElementById('p-nom').value;
        const niveauHierarchique = Number(document.getElementById('p-niv').value);
        const salaireBaseRecommande = Number(document.getElementById('p-sal').value);
        const description = document.getElementById('p-desc').value;

        if (!nom) {
          Swal.showValidationMessage('Le nom est obligatoire');
          return false;
        }
        return { nom, niveauHierarchique, salaireBaseRecommande, description };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(`${API}/rh/postes`, result.value, { headers: headers() });
          Swal.fire('Succès', 'Poste créé !', 'success');
          fetchPostes();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible de créer le poste.', 'error');
        }
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({ title: 'Supprimer ce poste ?', icon: 'warning', showCancelButton: true }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.delete(`${API}/rh/postes/${id}`, { headers: headers() });
        fetchPostes();
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ background: '#0F2343', width: '45px', height: '45px', borderRadius: '12px' }}>
              <Briefcase size={24} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Répertoire des Postes</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>Définition des rôles et hiérarchies</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="rh-search-wrapper" style={{ width: '250px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher un poste..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddPoste}>
              <Plus size={18} /> Ajouter
            </button>
          </div>
        </div>

        <div className="rh-table-container">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Intitulé du Poste</th>
                <th>Niveau</th>
                <th>Salaire Recommandé</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPostes.map(p => (
                <tr key={p._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                        <Briefcase size={16} />
                      </div>
                      <span className="rh-user-name">{p.nom}</span>
                    </div>
                  </td>
                  <td>
                    <div className="rh-period-text">
                      <Layers size={13} /> Niveau {p.niveauHierarchique}
                    </div>
                  </td>
                  <td>
                    <span className="rh-amount-total" style={{ background: '#F1F5F9', color: '#475569', padding: '4px 10px', borderRadius: '6px' }}>
                      {p.salaireBaseRecommande?.toLocaleString() || '—'} TND
                    </span>
                  </td>
                  <td>
                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.description || 'Aucune description'}
                    </p>
                  </td>
                  <td>
                    <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                      <button className="rh-action-btn edit" onClick={() => Swal.fire('Détails', p.description || 'Aucune description.', 'info')}>
                        <Info size={16} />
                      </button>
                      <button className="rh-action-btn delete" onClick={() => handleDelete(p._id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
